// Styled-components wrappers for app pages
import styled, { keyframes } from 'styled-components';
import { Card, Button } from 'antd';
import { Link as RouterLink } from 'react-router-dom';

export const Spinner = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 100vh;
`;

export const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-end;
    width: 100%;
    max-width: 600px;
    gap: ${({ theme }) => theme.spacing.md};

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        justify-content: center;
    }
`;

export const BreadcrumbWrapper = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    margin-top: 16px;
    width: 100vw;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
    padding: 0 ${({ theme }) => theme.spacing.lg};
    overflow-x: auto;
    overflow-y: hidden;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        overflow-x: visible;
        overflow-y: visible;
    }

    .ant-breadcrumb {
        width: 100%;
        max-width: 100%;
        display: flex;
        flex-wrap: nowrap;
        min-width: fit-content;

        @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
            flex-wrap: wrap;
            min-width: auto;
            justify-content: center;
        }
    }

    .ant-breadcrumb ol {
        display: flex;
        flex-wrap: nowrap;
        width: 100%;
        justify-content: center;
        min-width: fit-content;

        @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
            flex-wrap: wrap;
            min-width: auto;
            justify-content: center;
        }
    }

    .ant-breadcrumb li {
        flex-shrink: 0;
        white-space: nowrap;

        @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
            flex-shrink: 1;
        }
    }

    .ant-breadcrumb-link {
        color: var(--ant-breadcrumb-link-color, ${({ theme }) => theme.colors.text.secondary}) !important;
        white-space: nowrap;
    }

    .ant-breadcrumb-separator {
        color: var(--ant-breadcrumb-separator-color, ${({ theme }) => theme.colors.text.secondary}) !important;
        flex-shrink: 0;
        margin: 0 8px;
    }
`;

export const LinkWrapper = styled.div`
    text-align: center;
    margin-top: 16px;
`;

export const StyledLink = styled(RouterLink)`
    color: ${({ theme }) => {
        // Check if dark mode by background color
        const isDark = theme.colors.background.default === '#141414';
        return isDark ? 'rgba(135, 206, 250, 0.85)' : theme.colors.text.link;
    }};
    text-decoration: none;
    transition: color 0.2s;

    &:hover {
        color: ${({ theme }) => {
            // Check if dark mode by background color
            const isDark = theme.colors.background.default === '#141414';
            return isDark ? 'rgba(173, 216, 230, 1)' : theme.colors.linkHover;
        }};
    }
`;

export const MessageText = styled.p`
    margin: 0;
`;

export const AuthHiddenAutofillTrap = styled.div`
    position: absolute;
    left: -9999px;
    top: -9999px;
    width: 1px;
    height: 1px;
    overflow: hidden;
`;

export const AuthCard = styled(Card)`
    width: 100%;
    max-width: 420px;
    background: transparent;
    border: none;
    box-shadow: none;

    .ant-card-head {
        background: transparent;
    }

    .ant-card-body {
        padding: ${({ theme }) => theme.spacing.xl};
        display: flex;
        flex-direction: column;
        gap: ${({ theme }) => theme.spacing.lg};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
        max-width: 100%;
    }
`;

export const PreloadOverlayWrapper = styled.div`
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: ${({ theme }) => theme.spacing.lg};
    z-index: ${({ theme }) => theme.zIndex.modal + 99};
`;

export const PreloadOverlayCard = styled.div`
    width: 100%;
    max-width: 520px;
    background: ${({ theme }) => theme.colors.background.default};
    border-radius: ${({ theme }) => theme.borderRadius.lg};
    padding: ${({ theme }) => theme.spacing.xl};
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.35);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme.spacing.md};
    text-align: center;
`;

export const PreloadOverlayActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${({ theme }) => theme.spacing.md};
    flex-wrap: wrap;
`;

export const MobileLogoImage = styled.img`
    display: none;

    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
        display: block;
        width: 150px;
        margin: 25px auto 0 auto;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        width: 130px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
        width: 110px;
    }
`;

export const BannerPanel = styled.div`
    width: 100%;
    height: 100%;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0;
    padding: ${({ theme }) => theme.spacing.xl};
    background: #f04a4a;
    border-radius: 0 25px 25px 0;

    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
        display: none;
    }
`;

export const LogoImage = styled.img`
    width: 130px;
    height: auto;
    object-fit: contain;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        width: 180px;
    }
`;

export const BannerImage = styled.img`
    width: 100%;
    max-width: 360px;
    height: auto;
    object-fit: contain;
    display: block;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        max-width: 340px;
    }
`;

export const IconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
`;

export const BannerHeadline = styled.h1`
    font-family: var(--font-family-geist);
    font-weight: var(--font-weight-geist-bold);
    font-size: 2.2rem;
    color: #ffffff;
    text-align: center;
    margin: 20px 0 0 0;
`;

export const BannerSubheadline = styled.p`
    font-family: var(--font-family-geist);
    font-weight: var(--font-weight-geist-regular);
    font-size: 1rem;
    color: rgba(255, 255, 255, 0.85);
    text-align: center;
    margin: -5px 0 0 0;
    max-width: 360px;
`;

export const FormWrapper = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    order: 2;
    width: 100%;
    ${({ $centered }) => $centered && 'min-height: 100vh;'}

    .ant-form-item:last-child {
        margin-bottom: 0;
        margin-top: 8px;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
        width: 100%;
        min-height: 100vh;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2;
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        padding: ${({ theme }) => theme.spacing.xl};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        padding: ${({ theme }) => theme.spacing.lg};
    }
`;

export const FormInner = styled.div`
    display: flex;
    justify-content: center;
    align-items: flex-start;
    width: 100%;
    max-width: 320px;

    .ant-card {
        width: 100%;
        max-width: 420px;
        background: transparent;
        border: none;
        box-shadow: none;
        padding: 0;
        margin: 0 auto;
    }

    .ant-card-head {
        border-bottom: none;
        padding: 0;
        margin-bottom: ${({ theme }) => theme.spacing.md};
        text-align: center;
        background: transparent;
    }

    .ant-card-head-title {
        font-size: 2.25rem;
        font-weight: 700;
        line-height: 1.5;
        letter-spacing: -0.02em;
        /* Color uses Ant Design CSS variable that adapts to theme mode */
        color: var(--ant-color-text-heading);
    }

    .ant-card-body {
        padding: ${({ theme }) => theme.spacing.xl} 0 0;
        display: flex;
        flex-direction: column;
        align-items: stretch;
    }

    .auth-form-helper {
        text-align: right;
        margin: 0;
    }
`;

export const NoAccountLink = styled.p`
    text-align: center;
    margin-top: 16px;
    width: 100%;
`;

// Login page (reference design) – light mode, centered form
export const LoginRoot = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    max-width: 400px;
    margin: 0 auto;
`;

export const LoginBrandLogo = styled.img`
    width: 30px;
    height: auto;
    object-fit: contain;
    margin: 0 0 10px 0;
`;

export const LoginWelcome = styled.h1`
    font-size: 1.75rem;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0 0 8px 0;
    text-align: center;
`;

export const LoginSubtitle = styled.p`
    font-size: 0.9375rem;
    color: rgba(0, 0, 0, 0.55);
    margin: 0 0 24px 0;
    text-align: center;
`;

export const LoginLink = styled(RouterLink)`
    color: #7c3aed;
    text-decoration: none;
    font-weight: 600;
    &:hover {
        color: #6d28d9;
        text-decoration: underline;
    }
`;

export const LoginSocialBtn = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 12px 16px;
    font-size: 0.9375rem;
    font-weight: 500;
    color: #1a1a1a;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    cursor: pointer;
    transition:
        background 0.2s,
        border-color 0.2s;
    margin-bottom: 12px;
    &:hover {
        background: #f9fafb;
        border-color: #d1d5db;
    }
`;

export const LoginOr = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
    margin: 8px 0 24px 0;
    font-size: 0.875rem;
    color: rgba(0, 0, 0, 0.45);
    &::before,
    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: #e5e7eb;
    }
`;

export const LoginInputWrap = styled.div`
    width: 100%;
    margin-bottom: 16px;
    .ant-input,
    .ant-input-affix-wrapper {
        border-radius: 10px;
        border: 1px solid #c4b5fd;
        padding: 12px 14px;
        font-size: 0.9375rem;
    }
    .ant-input:focus,
    .ant-input-affix-wrapper:focus,
    .ant-input-affix-wrapper-focused {
        border-color: #8b5cf6;
        box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.15);
    }
`;

export const LoginFormWrapper = styled.div`
    width: 100%;
    .ant-form-item:last-child {
        margin-bottom: 0;
    }
    .ant-input,
    .ant-input-affix-wrapper {
        border-radius: 10px;
        border: 1px solid #c4b5fd;
        padding: 12px 14px;
        font-size: 0.9375rem;
    }
    .ant-input:focus,
    .ant-input-affix-wrapper:focus,
    .ant-input-affix-wrapper-focused {
        border-color: #8b5cf6;
        box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.15);
    }
`;

export const LoginSubmitBtn = styled.button`
    width: 100%;
    padding: 14px 16px;
    font-size: 1rem;
    font-weight: 600;
    color: #fff;
    background: #8b5cf6;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    margin-top: 8px;
    transition: background 0.2s;
    &:hover:not(:disabled) {
        background: #7c3aed;
    }
    &:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }
`;

export const LoginSubmitButton = styled(Button)`
    width: 100% !important;
    height: 48px !important;
    font-size: 1rem !important;
    font-weight: 600 !important;
    color: #fff !important;
    background: #8b5cf6 !important;
    border: none !important;
    border-radius: 10px !important;
    margin-top: 8px !important;
    &:hover:not(:disabled) {
        background: #7c3aed !important;
    }
`;

const dotWave = keyframes`
    0%, 80%, 100% { transform: translateY(0); opacity: 0.55; }
    40% { transform: translateY(-3px); opacity: 1; }
`;

export const DotWaveLoader = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    height: 16px;

    span {
        width: 6px;
        height: 6px;
        border-radius: 999px;
        background: rgba(255, 255, 255, 0.95);
        animation: ${dotWave} 1s ease-in-out infinite;
    }

    span:nth-child(2) {
        animation-delay: 0.12s;
    }
    span:nth-child(3) {
        animation-delay: 0.24s;
    }
`;

export const LoginForgot = styled(RouterLink)`
    display: block;
    text-align: center;
    margin-top: 16px;
    font-size: 0.9375rem;
    color: #7c3aed;
    text-decoration: none;
    &:hover {
        color: #6d28d9;
        text-decoration: underline;
    }
`;

export const ErrorPageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    width: 100%;
    padding: ${({ theme }) => theme.spacing.xl};
    text-align: center;
`;

export const ErrorIllustration = styled.div`
    margin-bottom: ${({ theme }) => theme.spacing.xl};
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const ErrorCode = styled.h1`
    font-family: var(--font-family-geist);
    font-weight: var(--font-weight-geist-bold);
    font-size: 6rem;
    color: #f04a4a;
    margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
    line-height: 1.2;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        font-size: 3rem;
    }
`;

export const ErrorMessage = styled.p`
    font-family: var(--font-family-geist);
    font-weight: var(--font-weight-geist-regular);
    font-size: 1.1rem;
    color: ${({ theme }) => theme.colors.text.secondary};
    margin: 0 0 ${({ theme }) => theme.spacing.xl} 0;
    max-width: 500px;

    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
        font-size: 1rem;
    }
`;

export const ErrorActionButton = styled.div`
    margin-top: ${({ theme }) => theme.spacing.lg};
`;
